import React from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';

import {
  hasAssemblyPermission as hasAssemblyPermissionSelector,
  hasPricingPermission as hasPricingPermissionSelector,
} from '@paragon/login-tools';
import { createNominalLumberDimensionFormatter } from '@paragon/material-lists';

import { RootState } from '../../../store';
import { moveToNextComponent, moveToPreviousComponent } from '../../../store/actions/selectActions';
import { getComponentLabelInformation } from '../../../store/selectors/componentLabelInformationSelector';
import {
  hasNextComponentDesign,
  hasPreviousComponentDesign,
} from '../../../store/selectors/componentNavigationSelectors';
import { isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import { getPlateManufacturerFormatter } from '../../../store/selectors/platePairFormatters';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ColoredComponentName } from '../../interface/panels/navigation-panel/name-formatters/ColoredComponentName';
import { StandardFloatingBox } from './common/StandardFloatingBox';

function getDisplayTextFromArray<T>(array: T[], formatter: (t: T) => string) {
  return array.length === 0 ? 'None' : array.length > 1 ? 'Varied' : formatter(array[0]);
}

function hasOtherComponents(state: RootState) {
  return hasPreviousComponentDesign(state) || hasNextComponentDesign(state);
}

export function ComponentLabelFloatingBox() {
  const dispatch = useAppDispatch();

  const hasAssemblyPermission = useAppSelector(hasAssemblyPermissionSelector);
  const hasPricingPermission = useAppSelector(hasPricingPermissionSelector);

  const information = useAppSelector(getComponentLabelInformation);
  const sequentialNavigationEnabled = useAppSelector(hasOtherComponents);
  const layoutViewActive = useAppSelector(isLayoutViewActive);
  const disablePrevious = useAppSelector((state) => !hasPreviousComponentDesign(state));
  const disableNext = useAppSelector((state) => !hasNextComponentDesign(state));
  const formatLumberDimensions = useAppSelector(createNominalLumberDimensionFormatter);
  const plateManufacturerFormatter = useAppSelector(getPlateManufacturerFormatter);

  if (!hasAssemblyPermission || !information || layoutViewActive) return null;

  let price;
  if (hasPricingPermission) {
    const priceTypography = (
      <Typography variant="caption" sx={{ display: 'block' }}>
        Price: {information.priceInformation.priceText}
      </Typography>
    );
    price = information.priceInformation.priceUnavailableReason ? (
      <Tooltip disableInteractive title={information.priceInformation.priceUnavailableReason}>
        {priceTypography}
      </Tooltip>
    ) : (
      priceTypography
    );
  }

  return (
    <StandardFloatingBox
      sx={{
        userSelect: 'none',
        textAlign: 'center',
      }}
    >
      {sequentialNavigationEnabled && (
        <IconButton disabled={disablePrevious} onClick={() => dispatch(moveToPreviousComponent())}>
          <ArrowUpwardIcon />
        </IconButton>
      )}

      <Box sx={{ position: 'relative', textAlign: 'center' }}>
        <Typography variant="h6" sx={{ display: 'inline' }}>
          <ColoredComponentName componentDesignGuid={information.componentDesignGuid}>
            {information.componentName}
            {information.quantity > 1 ? ` (${information.quantity})` : null}
          </ColoredComponentName>
        </Typography>
      </Box>

      <Typography variant="caption" sx={{ display: 'block' }}>
        TC: {getDisplayTextFromArray(information.topChordLumbers, formatLumberDimensions)}
      </Typography>

      <Typography variant="caption" sx={{ display: 'block' }}>
        BC: {getDisplayTextFromArray(information.bottomChordLumbers, formatLumberDimensions)}
      </Typography>

      <Typography variant="caption" sx={{ display: 'block' }}>
        Thickness:{' '}
        {information.thickness != null
          ? `${information.thickness}"${information.numberOfPlies > 1 ? ' (per ply)' : ''}`
          : 'Varied'}
      </Typography>

      {information.numberOfPlies > 1 && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          Plies: {information.numberOfPlies}
          {information.multiPlyConnectionType ? ` (${information.multiPlyConnectionType.toLowerCase()})` : ''}
        </Typography>
      )}

      <Typography variant="caption" sx={{ display: 'block' }}>
        Plates:{' '}
        {getDisplayTextFromArray(information.plateManufacturers, (plateManufacturer) =>
          plateManufacturerFormatter(plateManufacturer),
        )}
      </Typography>

      {price}

      {sequentialNavigationEnabled && (
        <IconButton disabled={disableNext} onClick={() => dispatch(moveToNextComponent())}>
          <ArrowDownwardIcon />
        </IconButton>
      )}
    </StandardFloatingBox>
  );
}
